import React, { useState } from 'react';
import {
  useSpaceView, addViewTile, saveViewTiles, saveViewNotes, useItemAssets,
} from '../data/firestore';
import AssetImage from './AssetImage';
import { TileImage, Lightbox } from './Moodboards';
import { clipFromWeb, isUploadConfigured } from '../data/uploadService';

// One technical view of a space (blueprints, electrical, plumbing, …) on a
// room page: an image board + notes. Images come from the space's uploaded
// photos or get clipped from any link; notes hold specs, measurements,
// contractor callouts. Doc created lazily at spaces/{id}/views/{viewId}.

const IMAGE_RX = /\.(jpe?g|png|gif|webp|avif)(\?.*)?$/i;

function NotesBlock({ spaceId, viewId, view, canEdit }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const notes = view?.notes || '';

  if (!canEdit) {
    return notes ? <p className="cs-help" style={{ whiteSpace: 'pre-wrap', maxWidth: 720 }}>{notes}</p> : null;
  }
  if (!editing) {
    return (
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', maxWidth: 720 }}>
        {notes
          ? <p className="cs-help" style={{ whiteSpace: 'pre-wrap', margin: 0, flex: 1 }}>{notes}</p>
          : <span className="cs-help" style={{ fontSize: 12, flex: 1 }}>No specs noted for this view yet.</span>}
        <button
          className="cs-btn cs-btn-ghost cs-btn-sm"
          onClick={() => { setDraft(notes); setEditing(true); }}
        >
          {notes ? 'Edit specs' : '+ Add specs'}
        </button>
      </div>
    );
  }
  return (
    <div style={{ maxWidth: 720 }}>
      <textarea
        className="cs-input"
        rows={6}
        style={{ width: '100%', resize: 'vertical' }}
        placeholder="Specs for this view — dimensions, materials, fixture models, code notes…"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        autoFocus
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
        <button
          className="cs-btn cs-btn-primary cs-btn-sm"
          onClick={async () => { await saveViewNotes(spaceId, viewId, draft); setEditing(false); }}
        >
          Save
        </button>
        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setEditing(false)}>Cancel</button>
      </div>
    </div>
  );
}

export default function ViewPanel({ spaceId, viewId, viewLabel, canEdit }) {
  const { doc: view } = useSpaceView(spaceId, viewId);
  const { docs: assets } = useItemAssets(spaceId);
  const [adding, setAdding] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [clipping, setClipping] = useState(false);
  const [clipError, setClipError] = useState('');
  const [openTile, setOpenTile] = useState(null);

  const tiles = view?.tiles || [];
  const photoAssets = (assets || []).filter((a) => a.kind === 'photo' && a.s3Key);
  const usedKeys = new Set(tiles.filter((t) => t.type === 'asset').map((t) => t.s3Key));

  const patchTile = (id, patch) =>
    saveViewTiles(spaceId, viewId, tiles.map((t) => (t.id === id ? { ...t, ...patch } : t)));
  const removeTile = (id) =>
    saveViewTiles(spaceId, viewId, tiles.filter((t) => t.id !== id));

  const addFromLink = async (url) => {
    setClipError('');
    if (isUploadConfigured()) {
      setClipping(true);
      try {
        const clip = await clipFromWeb({ url, spaceId });
        await addViewTile(spaceId, viewId, {
          type: 'asset',
          s3Key: clip.s3Key,
          url: clip.sourceUrl,
          name: clip.title || url.split('/').pop().split('?')[0] || '',
        });
        return true;
      } catch (err) {
        if (!IMAGE_RX.test(url)) {
          setClipError(err.message || 'Could not find an image at that link.');
          return false;
        }
      } finally {
        setClipping(false);
      }
    }
    if (!IMAGE_RX.test(url)) {
      setClipError('Could not clip that page — paste a direct image URL instead.');
      return false;
    }
    await addViewTile(spaceId, viewId, { type: 'url', url, name: url.split('/').pop().split('?')[0] });
    return true;
  };

  return (
    <div>
      <NotesBlock spaceId={spaceId} viewId={viewId} view={view} canEdit={canEdit} />

      {canEdit && (
        <div style={{ marginTop: 10 }}>
          <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => setAdding((v) => !v)}>
            {adding ? 'Close' : `+ Add ${viewLabel.toLowerCase()} images`}
          </button>
          {adding && (
            <div style={{ marginTop: 10 }}>
              <form
                style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const url = urlInput.trim();
                  if (!url || clipping) return;
                  if (await addFromLink(url)) setUrlInput('');
                }}
              >
                <input
                  className="cs-input"
                  style={{ flex: '2 1 260px' }}
                  placeholder="Paste a link — plan PDF preview, diagram, image URL…"
                  value={urlInput}
                  onChange={(e) => { setUrlInput(e.target.value); setClipError(''); }}
                />
                <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!urlInput.trim() || clipping}>
                  {clipping ? 'Clipping…' : 'Clip it'}
                </button>
              </form>
              {clipError && (
                <p className="cs-help" style={{ margin: '8px 0 0', fontSize: 12, color: '#c0392b' }}>{clipError}</p>
              )}
              {photoAssets.length > 0 && (
                <>
                  <p className="cs-help" style={{ margin: '12px 0 6px', fontSize: 12 }}>
                    …or pick from this space's uploaded photos:
                  </p>
                  <div className="ah-mood-picker">
                    {photoAssets.map((a) => (
                      <button
                        key={a.id}
                        type="button"
                        className={`ah-mood-pick ${usedKeys.has(a.s3Key) ? 'used' : ''}`}
                        title={usedKeys.has(a.s3Key) ? `${a.name} (already on this view)` : a.name}
                        disabled={usedKeys.has(a.s3Key)}
                        onClick={() => addViewTile(spaceId, viewId, { type: 'asset', s3Key: a.s3Key, name: a.name })}
                      >
                        <AssetImage s3Key={a.s3Key} alt={a.name} />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {!tiles.length && (
        <div className="cs-empty" style={{ marginTop: 12, padding: 14 }}>
          No {viewLabel.toLowerCase()} images yet
          {canEdit ? ' — clip a link or pick from the space\u2019s uploads.' : '.'}
        </div>
      )}

      {tiles.length > 0 && (
        <div className="ah-mood-grid">
          {tiles.map((t) => (
            <figure key={t.id} className="ah-mood-tile" onClick={() => setOpenTile(t)}>
              <TileImage tile={t} />
              {(t.note || t.name) && <figcaption>{t.note || t.name}</figcaption>}
            </figure>
          ))}
        </div>
      )}

      {openTile && (
        <Lightbox
          tile={openTile}
          canEdit={canEdit}
          onClose={() => setOpenTile(null)}
          onSaveNote={(note) => patchTile(openTile.id, { note })}
          onRemove={() => removeTile(openTile.id)}
        />
      )}
    </div>
  );
}
